import { z } from "zod";

/**
 * Element annotator — RPC schemas.
 *
 * The daemon drives a host-side Playwright Chromium page and mirrors what it
 * renders into a client popup ("the sandbox of what the host sees"). The user
 * taps an element; the daemon resolves the real CSS selector + a cropped
 * highlight screenshot so the agent knows exactly which element to fix.
 *
 * Everything rides the existing relay WebSocket — frames and element crops are
 * base64-encoded PNGs (no data-URI prefix) carried in the RPC payloads. There
 * is no daemon HTTP path, so this works for remote hosts over the relay.
 *
 * Frames are captured at deviceScaleFactor 1, so 1 CSS pixel === 1 image pixel
 * and the client can map taps directly to viewport coordinates.
 */

/** A rendered viewport frame returned by open / frame / scroll. */
export const PreviewFrameSchema = z.object({
  /** Base64 PNG of the current viewport (no `data:` prefix). */
  image: z.string(),
  /** Viewport width in CSS px (== image px at scale 1). */
  width: z.number(),
  /** Viewport height in CSS px (== image px at scale 1). */
  height: z.number(),
  /** Current horizontal scroll offset of the page in CSS px. */
  scrollX: z.number(),
  /** Current vertical scroll offset of the page in CSS px. */
  scrollY: z.number(),
  /** Full scrollable page height in CSS px (for a scroll indicator). */
  pageHeight: z.number(),
  /** Document title of the loaded page, if any. */
  title: z.string().nullable(),
  /** Resolved final URL after redirects. */
  url: z.string(),
});

/** A picked element resolved from a viewport tap. */
export const PreviewElementSchema = z.object({
  /** Robust CSS selector pointing at the element. */
  selector: z.string(),
  /** Full DOM ancestor breadcrumb for context, e.g. `body > main > h2#title`. */
  path: z.string().optional().nullable(),
  /** Lowercased tag name, e.g. "button". */
  tag: z.string(),
  /** Trimmed, truncated visible text content. */
  text: z.string(),
  /** Truncated outerHTML for context. */
  html: z.string(),
  /** Bounding rect in viewport CSS px. */
  rect: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  }),
  /** Base64 PNG crop of the element with a highlight outline (no `data:` prefix). */
  image: z.string(),
});

export const PreviewOpenRequestSchema = z.object({
  type: z.literal("preview/open"),
  requestId: z.string(),
  url: z.string(),
  /** Optional initial viewport in CSS px (defaults to 1280x800). */
  width: z.number().optional(),
  height: z.number().optional(),
});

/** Forwarded real input event (interact mode). Fire-and-forget. */
export const PreviewInputRequestSchema = z.object({
  type: z.literal("preview/input"),
  sessionId: z.string(),
  action: z.enum(["move", "down", "up", "click", "wheel", "key"]),
  /** Pointer position in viewport CSS px (move/down/up/click). */
  x: z.number().optional(),
  y: z.number().optional(),
  /** Wheel deltas in CSS px (wheel). */
  dx: z.number().optional(),
  dy: z.number().optional(),
  /** Key name for press, e.g. "Enter", "Backspace" (key). */
  key: z.string().optional(),
  /** Literal text to type (key). */
  text: z.string().optional(),
});

/** Resize the host's rendered viewport (devtools-style). */
export const PreviewResizeRequestSchema = z.object({
  type: z.literal("preview/resize"),
  requestId: z.string(),
  sessionId: z.string(),
  width: z.number(),
  height: z.number(),
});

export const PreviewFrameRequestSchema = z.object({
  type: z.literal("preview/frame"),
  requestId: z.string(),
  sessionId: z.string(),
});

export const PreviewScrollRequestSchema = z.object({
  type: z.literal("preview/scroll"),
  requestId: z.string(),
  sessionId: z.string(),
  /** Wheel delta in CSS px. */
  dx: z.number(),
  dy: z.number(),
});

export const PreviewPickRequestSchema = z.object({
  type: z.literal("preview/pick"),
  requestId: z.string(),
  sessionId: z.string(),
  /** Tap position in viewport CSS px. */
  x: z.number(),
  y: z.number(),
});

export const PreviewCloseRequestSchema = z.object({
  type: z.literal("preview/close"),
  requestId: z.string(),
  sessionId: z.string(),
});

export const PreviewOpenResponseSchema = z.object({
  type: z.literal("preview/open/response"),
  payload: z.object({
    requestId: z.string(),
    sessionId: z.string().nullable(),
    frame: PreviewFrameSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const PreviewFrameResponseSchema = z.object({
  type: z.literal("preview/frame/response"),
  payload: z.object({
    requestId: z.string(),
    frame: PreviewFrameSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const PreviewScrollResponseSchema = z.object({
  type: z.literal("preview/scroll/response"),
  payload: z.object({
    requestId: z.string(),
    frame: PreviewFrameSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const PreviewPickResponseSchema = z.object({
  type: z.literal("preview/pick/response"),
  payload: z.object({
    requestId: z.string(),
    element: PreviewElementSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const PreviewCloseResponseSchema = z.object({
  type: z.literal("preview/close/response"),
  payload: z.object({
    requestId: z.string(),
    error: z.string().nullable(),
  }),
});

export const PreviewResizeResponseSchema = z.object({
  type: z.literal("preview/resize/response"),
  payload: z.object({
    requestId: z.string(),
    width: z.number(),
    height: z.number(),
    error: z.string().nullable(),
  }),
});

/**
 * Server→client streamed screencast frame (push, not a response). The client
 * subscribes via subscribeRawMessages and renders frames for its sessionId.
 */
export const PreviewFramePushSchema = z.object({
  type: z.literal("preview/frame_push"),
  payload: z.object({
    sessionId: z.string(),
    /** Base64 JPEG of the current viewport (no `data:` prefix). */
    data: z.string(),
    /** Viewport size in CSS px the frame was captured at. */
    width: z.number(),
    height: z.number(),
    /** Page scroll offset (CSS px) when this frame was captured. */
    scrollX: z.number().optional(),
    scrollY: z.number().optional(),
  }),
});

export type PreviewFrame = z.infer<typeof PreviewFrameSchema>;
export type PreviewElement = z.infer<typeof PreviewElementSchema>;
